import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowLeft, ArrowRight, Search, ShieldCheck, Target, AlertCircle, Unlock } from "lucide-react";

const HOW_IT_WORKS = [
  {
    icon: Search,
    title: "Ask in plain language",
    body: "Describe what you are working on and what you need. ConnectMakeLaunchAI returns the specific programs that fit, along with contact details and a short explanation of why each one is relevant.",
  },
  {
    icon: ShieldCheck,
    title: "Curated and kept current",
    body: "Recommendations come only from a database of regional resources, not from an open internet search. The database is maintained by staff, and every entry is reviewed by a person before it reaches the AI layer.",
  },
  {
    icon: Target,
    title: "Program-level results",
    body: "Rather than pointing to an organization in general, it identifies the specific grant, service, cohort, or facility that matches your request.",
  },
  {
    icon: AlertCircle,
    title: "Honest about gaps",
    body: "If there is no strong match for a need, the tool says so. Identifying gaps helps us understand what additional resources or information the region needs.",
  },
  {
    icon: Unlock,
    title: "Open to everyone",
    body: "Search is available without an account. An optional free account lets you save results and send a request for information directly to Innovation Accelerator Network moderators.",
  },
];

const SERVES = [
  "Startups", "Entrepreneurs", "Main Street businesses", "Established companies",
  "Manufacturers", "Individuals looking to move forward",
];

const CONNECTS = [
  "Funding opportunities", "Business assistance", "Accelerators", "Makerspaces",
  "Manufacturing & technology resources", "Workforce-development programs", "Hiring pipelines",
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button className="flex items-center gap-3" onClick={() => navigate('/')} data-testid="about-home-btn">
            <div className="w-9 h-9 bg-[#c9a962] rounded-md flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#1e3a5f]" />
            </div>
            <span className="font-bold text-lg text-[#f5f0e6]">ConnectMakeLaunch AI</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm text-[#f5f0e6]/80 hover:text-[#f5f0e6] transition-colors"
              data-testid="about-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#c9a962] text-[#1e3a5f] text-sm font-semibold hover:bg-[#d8bd7e] transition-colors"
              data-testid="about-start-chatting-btn"
            >
              Start Chatting <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Intro */}
      <section className="max-w-3xl mx-auto px-6 pt-16 pb-10">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#c9a962] mb-4">About</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#1e3a5f] leading-[1.1] tracking-tight">
          About ConnectMakeLaunchAI
        </h1>
        <p className="text-base md:text-lg text-[#1e3a5f]/80 leading-relaxed mt-6">
          ConnectMakeLaunchAI helps people and businesses find the business, entrepreneurship, manufacturing,
          and workforce resources available across Southwestern Pennsylvania.
        </p>
        <p className="text-base text-[#1e3a5f]/70 leading-relaxed mt-5">
          The region has a strong network of organizations and programs, but knowing which one fits a specific
          need can be difficult. Support is spread across universities, county economic development offices,
          nonprofits, state agencies, and private accelerators, each with its own eligibility rules and point of
          contact. ConnectMakeLaunchAI brings that information into one place and makes it easier to find the
          right match.
        </p>
      </section>

      {/* Who it serves / what it connects */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-[#1e3a5f] rounded-2xl p-7" data-testid="about-serves">
            <h2 className="text-base md:text-lg font-bold text-[#f5f0e6] mb-4">Who it serves</h2>
            <div className="flex flex-wrap gap-2">
              {SERVES.map(s => (
                <span key={s} className="text-sm text-[#1e3a5f] bg-[#c9a962] px-3 py-1 rounded-full">{s}</span>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl p-7 border border-[#e8e0d0]" data-testid="about-connects">
            <h2 className="text-base md:text-lg font-bold text-[#1e3a5f] mb-4">What it connects you with</h2>
            <div className="flex flex-wrap gap-2">
              {CONNECTS.map(s => (
                <span key={s} className="text-sm text-[#1e3a5f] bg-[#f5f0e6] border border-[#e8e0d0] px-3 py-1 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-[#e8e0d0] py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-base md:text-lg font-bold text-[#1e3a5f] mb-2">How it works</h2>
          <div className="w-16 h-0.5 bg-[#c9a962] mb-10" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            {HOW_IT_WORKS.map((item, i) => (
              <div key={item.title} className="flex gap-4" data-testid={`about-how-${i}`}>
                <div className="w-10 h-10 shrink-0 rounded-lg bg-[#f5f0e6] border border-[#e8e0d0] flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#c9a962]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1.5">{item.title}</h3>
                  <p className="text-sm text-[#1e3a5f]/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation Accelerator Network */}
      <section className="max-w-3xl mx-auto px-6 py-16" data-testid="about-network">
        <h2 className="text-base md:text-lg font-bold text-[#1e3a5f] mb-2">About the Innovation Accelerator Network</h2>
        <div className="w-16 h-0.5 bg-[#c9a962] mb-8" />
        <div className="space-y-5 text-[#1e3a5f]/75 leading-relaxed">
          <p className="text-base text-[#1e3a5f]">
            ConnectMakeLaunchAI is an initiative of the Innovation Accelerator Network and is maintained by RIDC
            of Southwestern Pennsylvania, with support from the Allegheny Conference on Community Development.
          </p>
          <p className="text-sm md:text-base">
            The Innovation Accelerator Network is part of the New Economy Collaborative of Southwestern
            Pennsylvania and grew out of the region's Build Back Better Regional Challenge initiative, funded by a
            $62.7 million U.S. Economic Development Administration grant awarded in 2022. That funding
            established a range of entrepreneurship and workforce programs focused on emerging industries,
            including Innovation Works' Robotics Factory accelerator, regional makerspaces, and the ARM
            Institute's Advanced Manufacturing Hub. The Innovation Accelerator Network's goal is to document
            these resources, along with other business and workforce resources across the region, and help people
            connect to them.
          </p>
          <p className="text-sm md:text-base">
            The network has since grown into a regional consortium of universities, makerspaces, business
            accelerators, economic-development organizations, workforce partners, and manufacturing and
            technology organizations. Its members share a commitment to supporting entrepreneurship, business
            growth, innovation, and workforce development across Southwestern Pennsylvania, and to making the
            region's resources easier to navigate.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1e3a5f] py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#f5f0e6] mb-4">Tell us what you're working on</h2>
          <p className="text-[#f5f0e6]/70 mb-8">No account needed to search. Ask a question and get matched programs.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 bg-[#c9a962] text-[#1e3a5f] font-semibold px-8 py-4 rounded-full hover:bg-[#d8bd7e] transition-colors"
            data-testid="about-cta-btn"
          >
            Start Chatting <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <footer className="border-t border-[#e8e0d0] py-8 bg-[#f5f0e6]">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-sm text-[#1e3a5f]/60">An initiative of the Innovation Accelerator Network · maintained by RIDC of Southwestern Pennsylvania</span>
          <button onClick={() => navigate('/')} className="text-sm text-[#1e3a5f] font-medium hover:text-[#c9a962] transition-colors" data-testid="about-footer-home-btn">
            Back to home
          </button>
        </div>
      </footer>
    </div>
  );
}
